// ============================================================================
// SYUT TIMESHEET GRID
// ============================================================================
// Reusable weekly timesheet summary grid component. Read-only display with
// entries grouped by charge type (C, NCP, NCNP) and day-wise subtotals.
//
// Week starts on WEDNESDAY: Wed, Thu, Fri, Sat, Sun, Mon, Tue
//
// Usage:
//   <SyutTimesheetGrid
//     weekDates={['2025-04-09', ...]}  // 7 dates Wed..Tue
//     entries={rows}
//     holidays={['2025-04-12','2025-04-13']}
//     minimumMinutesPerDay={{ '2025-04-09': 480, ... }}
//   />

import React, { useState, useMemo, useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import './SyutTimesheetGrid.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TimesheetEntryRow {
  id: string;
  chargeTypeKey: string;       // C, NCP, NCNP
  chargeTypeValue: string;     // Chargeable, Non-Chargeable Productive, etc.
  engagementId?: string;
  engagementLabel?: string;    // "Client - Type - Year" format
  activityId: string;
  activityName: string;
  remarks?: string;
  isEngagementClosed?: boolean;
  /** Hours per day keyed by YYYY-MM-DD → minutes */
  dayMinutes: Record<string, number>;
}

export interface SyutTimesheetGridProps {
  weekDates: string[];           // 7 date strings [Wed..Tue]
  entries: TimesheetEntryRow[];
  holidays: string[];            // Holiday dates (show icon, min=0)
  minimumMinutesPerDay: Record<string, number>; // date -> min minutes
  readOnly?: boolean;
  onEdit?: (entry: TimesheetEntryRow) => void;
  onDelete?: (entry: TimesheetEntryRow) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DAY_LABELS = ['Wed', 'Thu', 'Fri', 'Sat', 'Sun', 'Mon', 'Tue'];

const CHARGE_TYPE_ORDER: Record<string, number> = { C: 0, NCP: 1, NCNP: 2 };

const CHARGE_TYPE_CONFIG: Record<string, { icon: string; label: string }> = {
  C:    { icon: '\u2605', label: 'Chargeable' },          // ★
  NCP:  { icon: '\u25CB', label: 'Non-Chargeable Productive' },  // ○
  NCNP: { icon: '\u25CF', label: 'Non-Chargeable Non-Productive' }, // ●
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatMinutes(minutes: number): string {
  if (!minutes || minutes <= 0) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}

function formatDateShort(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return String(d.getDate()).padStart(2, '0');
}

function getDayLabel(dateStr: string, index: number): string {
  return `${DAY_LABELS[index]} ${formatDateShort(dateStr)}`;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

export interface SummaryBarProps {
  weekDates: string[];
  entries: TimesheetEntryRow[];
  holidays: string[];
  minimumMinutesPerDay: Record<string, number>;
}

export const SummaryBar: React.FC<SummaryBarProps> = ({ weekDates, entries, holidays, minimumMinutesPerDay }) => {
  const holidaySet = useMemo(() => new Set(holidays), [holidays]);

  const dayTotals = useMemo(() => {
    return weekDates.map(date => {
      const total = entries.reduce((sum, e) => sum + (e.dayMinutes[date] || 0), 0);
      const min = holidaySet.has(date) ? 0 : (minimumMinutesPerDay[date] || 0);
      const diff = total - min;
      return { date, total, min, diff };
    });
  }, [weekDates, entries, holidaySet, minimumMinutesPerDay]);

  const weeklyTotal = dayTotals.reduce((s, d) => s + d.total, 0);
  const weeklyMin = dayTotals.reduce((s, d) => s + d.min, 0);

  return (
    <div className="ts-summary-bar">
      <div className="ts-summary-row">
        {dayTotals.map((day, idx) => {
          const isHoliday = holidaySet.has(day.date);
          let diffColor = 'var(--text-muted)';
          if (!isHoliday && day.total > 0) {
            if (day.diff > 0) diffColor = '#d97706';
            else if (day.diff < 0) diffColor = '#dc2626';
            else diffColor = '#16a34a';
          }

          return (
            <div
              key={day.date}
              className="ts-summary-cell"
              style={{ background: isHoliday ? 'rgba(100,116,139,0.06)' : undefined }}
            >
              <div className="ts-summary-day-label">
                {DAY_LABELS[idx]} {formatDateShort(day.date)}
                {isHoliday && <span style={{ marginLeft: 3 }} title="Holiday">{'\uD83C\uDFD6\uFE0F'}</span>}
              </div>
              <div className="ts-summary-hours" style={{ fontWeight: 700, fontSize: '0.8125rem' }}>
                {formatMinutes(day.total)}
              </div>
              <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)' }}>
                min: {formatMinutes(day.min)}
              </div>
              {!isHoliday && day.min > 0 && (
                <div style={{ fontSize: '0.6rem', fontWeight: 600, color: diffColor }}>
                  {day.diff > 0 ? '+' : ''}{formatMinutes(Math.abs(day.diff))}
                  {day.diff < 0 ? ' under' : day.diff > 0 ? ' over' : ''}
                </div>
              )}
            </div>
          );
        })}
        <div className="ts-summary-cell ts-summary-total">
          <div className="ts-summary-day-label" style={{ fontWeight: 700 }}>Total</div>
          <div className="ts-summary-hours" style={{ fontWeight: 700, fontSize: '0.9rem' }}>
            {formatMinutes(weeklyTotal)}
          </div>
          <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)' }}>
            /{formatMinutes(weeklyMin)}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Charge Type Group
// ---------------------------------------------------------------------------

interface ChargeTypeGroupProps {
  chargeTypeKey: string;
  entries: TimesheetEntryRow[];
  weekDates: string[];
  holidays: string[];
  readOnly?: boolean;
  onEdit?: (entry: TimesheetEntryRow) => void;
  onDelete?: (entry: TimesheetEntryRow) => void;
}

const ChargeTypeGroup: React.FC<ChargeTypeGroupProps> = ({
  chargeTypeKey, entries, weekDates, holidays, readOnly, onEdit, onDelete,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const holidaySet = useMemo(() => new Set(holidays), [holidays]);
  const config = CHARGE_TYPE_CONFIG[chargeTypeKey] || { icon: '?', label: chargeTypeKey };

  // Day-wise subtotals for this group
  const groupDayTotals = useMemo(() => {
    return weekDates.map(date =>
      entries.reduce((sum, e) => sum + (e.dayMinutes[date] || 0), 0)
    );
  }, [weekDates, entries]);

  const groupTotal = groupDayTotals.reduce((s, v) => s + v, 0);

  return (
    <div className="ts-group" style={{ marginBottom: 0 }}>
      {/* Section header */}
      <div
        className="ts-group-header"
        onClick={() => setCollapsed(prev => !prev)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCollapsed(prev => !prev); } }}
        style={{ cursor: 'pointer', userSelect: 'none' }}
      >
        <div className="ts-group-header-left">
          <span style={{ marginRight: 4, fontSize: '0.65rem' }}>{collapsed ? '\u25B6' : '\u25BC'}</span>
          <span style={{ marginRight: 4, fontSize: '0.75rem' }}>{config.icon}</span>
          <span style={{ fontWeight: 600, fontSize: '0.75rem' }}>
            {config.label}
          </span>
          <span style={{ marginLeft: 6, fontSize: '0.675rem', color: 'var(--text-muted)' }}>
            ({entries.length})
          </span>
        </div>
        <div className="ts-group-header-right" style={{ display: 'flex', gap: '0', alignItems: 'center' }}>
          {groupDayTotals.map((total, idx) => (
            <div key={weekDates[idx]} className="ts-day-cell ts-day-cell-header" style={{
              background: holidaySet.has(weekDates[idx]) ? 'rgba(100,116,139,0.06)' : undefined,
            }}>
              {formatMinutes(total)}
            </div>
          ))}
          <div className="ts-day-cell ts-day-cell-total" style={{ fontWeight: 700 }}>
            {formatMinutes(groupTotal)}
          </div>
        </div>
      </div>

      {/* Entries */}
      {!collapsed && (
        <div className="ts-group-entries">
          {entries.length === 0 && (
            <div style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
              No entries in this category.
            </div>
          )}
          {entries.map(entry => (
            <EntryCard
              key={entry.id}
              entry={entry}
              weekDates={weekDates}
              holidays={holidays}
              readOnly={readOnly}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Entry Card
// ---------------------------------------------------------------------------

interface EntryCardProps {
  entry: TimesheetEntryRow;
  weekDates: string[];
  holidays: string[];
  readOnly?: boolean;
  onEdit?: (entry: TimesheetEntryRow) => void;
  onDelete?: (entry: TimesheetEntryRow) => void;
}

const EntryCard: React.FC<EntryCardProps> = ({ entry, weekDates, holidays, readOnly, onEdit, onDelete }) => {
  const holidaySet = useMemo(() => new Set(holidays), [holidays]);
  const rowTotal = weekDates.reduce((sum, date) => sum + (entry.dayMinutes[date] || 0), 0);

  return (
    <div className="ts-entry-card">
      {/* Top section: info + actions */}
      <div className="ts-entry-info">
        <div className="ts-entry-details">
          <div className="ts-entry-line1">
            {entry.isEngagementClosed && (
              <span title="Engagement closed" style={{ marginRight: 4 }}>{'\uD83D\uDD12'}</span>
            )}
            <span style={{ fontWeight: 600, fontSize: '0.7rem' }}>
              {entry.engagementLabel || entry.activityName}
            </span>
          </div>
          {entry.engagementLabel && (
            <div style={{ fontSize: '0.65rem', color: 'var(--text-secondary)', lineHeight: 1.2 }}>
              {entry.activityName}
            </div>
          )}
          {entry.remarks && (
            <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', fontStyle: 'italic', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2 }}>
              {entry.remarks}
            </div>
          )}
        </div>
        {!readOnly && (
          <div className="ts-entry-actions" style={{ display: 'flex', gap: '0.25rem', flexShrink: 0 }}>
            {onEdit && (
              <button
                className="modal-header-btn"
                title="Edit entry"
                onClick={() => onEdit(entry)}
                style={{ padding: '0.25rem' }}
              >
                <IconSystem.edit size={14} />
              </button>
            )}
            {onDelete && (
              <button
                className="modal-header-btn"
                title="Delete entry"
                onClick={() => onDelete(entry)}
                style={{ padding: '0.25rem' }}
              >
                <IconSystem.trash size={14} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Day hours row */}
      <div className="ts-entry-hours-row">
        {weekDates.map((date, idx) => (
          <div key={date} className="ts-day-cell" style={{
            background: holidaySet.has(date) ? 'rgba(100,116,139,0.06)' : undefined,
          }}>
            {formatMinutes(entry.dayMinutes[date] || 0)}
          </div>
        ))}
        <div className="ts-day-cell ts-day-cell-total" style={{ fontWeight: 600 }}>
          {formatMinutes(rowTotal)}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export const SyutTimesheetGrid: React.FC<SyutTimesheetGridProps> = ({
  weekDates,
  entries,
  holidays,
  minimumMinutesPerDay,
  readOnly = false,
  onEdit,
  onDelete,
}) => {
  // Group entries by charge type
  const groups = useMemo(() => {
    const map = new Map<string, TimesheetEntryRow[]>();
    // Initialize all 3 groups in order
    ['C', 'NCP', 'NCNP'].forEach(key => map.set(key, []));

    entries.forEach(entry => {
      const key = entry.chargeTypeKey || 'NCNP';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(entry);
    });

    return Array.from(map.entries())
      .sort((a, b) => (CHARGE_TYPE_ORDER[a[0]] ?? 99) - (CHARGE_TYPE_ORDER[b[0]] ?? 99));
  }, [entries]);

  // Empty state
  if (entries.length === 0) {
    return (
      <div className="ts-grid-container">
        <div style={{
          padding: '3rem 1.5rem',
          textAlign: 'center',
          color: 'var(--text-muted)',
          fontSize: '0.875rem',
        }}>
          No entries for this week. Click <strong>+ Add Entry</strong> to get started.
        </div>
      </div>
    );
  }

  return (
    <div className="ts-grid-container">
      {/* Grouped sections */}
      {groups.map(([chargeTypeKey, groupEntries]) => (
        <ChargeTypeGroup
          key={chargeTypeKey}
          chargeTypeKey={chargeTypeKey}
          entries={groupEntries}
          weekDates={weekDates}
          holidays={holidays}
          readOnly={readOnly}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
};


export default SyutTimesheetGrid;
